import type { Locale } from "~/i18n/index";
import { LOCALES } from "~/i18n/index";

export const SITE_URL = "https://caratzon.com";
export const SITE_NAME = "Caratzon";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

/** Marketing pages included in sitemap.xml */
export const MARKETING_PAGES = [
  "/",
  "/designers",
  "/brands",
  "/for-manufacturers",
  "/about",
  "/academy",
  "/brands/pricing",
  "/inspire",
  "/newsroom",
  "/newsroom/press-assets",
  "/contact",
  "/partner-application",
  "/terms",
  "/privacy",
] as const;

/** BCP 47 locale → OpenGraph underscore locale */
export const OG_LOCALE_MAP: Record<Locale, string> = {
  en: "en_US",
  "zh-HK": "zh_HK",
  "zh-CN": "zh_CN",
  ja: "ja_JP",
  ru: "ru_RU",
};

/** Build full canonical URL for a pathname (strips trailing slash except root) */
export function canonicalUrl(pathname: string): string {
  const clean = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  return `${SITE_URL}${clean}`;
}

/**
 * hreflang link descriptors for the `<head>`.
 * All locales point to the same URL (cookie-based locale selection).
 */
export function hreflangLinks(pathname: string) {
  const url = canonicalUrl(pathname);
  return [
    ...LOCALES.map((locale) => ({
      rel: "alternate" as const,
      hrefLang: locale,
      href: url,
    })),
    { rel: "alternate" as const, hrefLang: "x-default", href: url },
  ];
}

/**
 * OG + Twitter meta descriptors. Spread into a route's meta() return array.
 */
export function socialMeta({
  title,
  description,
  pathname,
  locale,
  image,
}: {
  title: string;
  description: string;
  pathname: string;
  locale: Locale;
  image?: string;
}) {
  const url = canonicalUrl(pathname);
  const ogImage = image ?? DEFAULT_OG_IMAGE;
  const ogLocale = OG_LOCALE_MAP[locale];

  return [
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:locale", content: ogLocale },
    ...LOCALES.filter((l) => l !== locale).map((l) => ({
      property: "og:locale:alternate",
      content: OG_LOCALE_MAP[l],
    })),
    { property: "og:image", content: ogImage },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
  ];
}

/**
 * Article JSON-LD structured data for Academy articles.
 */
export function articleJsonLd({
  title,
  description,
  slug,
  authorName,
  publishedAt,
  updatedAt,
  image,
}: {
  title: string;
  description: string;
  slug: string;
  authorName: string;
  publishedAt: string;
  updatedAt: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    author: { "@type": "Person", name: authorName },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    url: `${SITE_URL}/academy/${slug}`,
    datePublished: publishedAt,
    dateModified: updatedAt,
    image: image ?? DEFAULT_OG_IMAGE,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/academy/${slug}` },
  };
}

/**
 * NewsArticle JSON-LD structured data for Newsroom articles.
 */
export function newsArticleJsonLd({
  title,
  description,
  slug,
  authorName,
  publishedAt,
  updatedAt,
  image,
}: {
  title: string;
  description: string;
  slug: string;
  authorName: string;
  publishedAt: string;
  updatedAt: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: title,
    description,
    author: { "@type": "Organization", name: authorName },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    url: `${SITE_URL}/newsroom/${slug}`,
    datePublished: publishedAt,
    dateModified: updatedAt,
    image: image ?? DEFAULT_OG_IMAGE,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/newsroom/${slug}` },
  };
}

/**
 * BreadcrumbList JSON-LD.
 */
export function breadcrumbJsonLd(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
